import { CheckCircle2, ExternalLink } from "lucide-react";

export function BcbsilTakeawaysAndSources() {
  return (
    <section className="py-14 sm:py-20 bg-white border-b border-border">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 text-left">
        {/* Key Takeaways */}
        <div className="rounded-2xl p-6 sm:p-8 bg-muted border border-border mb-12">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-2.5 h-2.5 rounded-full bg-accent" />
            <span className="text-xs uppercase tracking-wider font-bold text-accent">
              Key Takeaways
            </span>
          </div>
          <h2 className="text-xl sm:text-2xl font-semibold text-primary mb-4">
            Summary for BCBS of Illinois members
          </h2>
          <ul className="space-y-3">
            {[
              "SAINT® is an FDA-cleared, MRI-guided TMS treatment for adults 22+ whose depression hasn't improved with antidepressants.",
              "Treatment takes five days, compared with 6 to 9 weeks for standard TMS.",
              "In a controlled clinical trial, 79% of participants reached remission. Individual results vary.",
              "SAINT may be available through your Blue Cross and Blue Shield of Illinois plan, for you and eligible family members.",
              "We verify your benefits and handle prior authorization, and you know your cost before you commit.",
            ].map((bullet, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-accent shrink-0 mt-0.5" />
                <span className="text-sm sm:text-base text-foreground leading-relaxed">
                  {bullet}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Medical Review & Sources */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 text-sm text-foreground">
          {/* Medical Review Column */}
          <div className="md:col-span-5 p-6 rounded-2xl bg-white border border-border">
            <h3 className="font-semibold text-primary text-base mb-3">
              Clinical Review Information
            </h3>
            <dl className="space-y-2 text-xs sm:text-sm">
              <div>
                <dt className="text-muted-foreground">Written by:</dt>
                <dd className="font-medium text-primary">
                  Neuro Wellness TMS Centers of America
                </dd>
              </div>
              <div className="pt-1 border-t border-border">
                <dt className="text-muted-foreground">
                  Medically reviewed by:
                </dt>
                <dd className="font-medium text-primary">
                  Daniel Pietras, MD, Board-Certified Psychiatrist, Medical
                  Director
                </dd>
              </div>
              <div className="pt-1 border-t border-border flex justify-between">
                <div>
                  <dt className="text-muted-foreground">Published:</dt>
                  <dd className="font-medium text-primary">November 3, 2026</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Last reviewed:</dt>
                  <dd className="font-medium text-primary">November 3, 2026</dd>
                </div>
              </div>
            </dl>
          </div>

          {/* Sources Column */}
          <div className="md:col-span-7 p-6 rounded-2xl bg-white border border-border">
            <h3 className="font-semibold text-primary text-base mb-3">
              Sources & Scientific References
            </h3>
            <ol className="space-y-2.5 text-xs sm:text-sm list-decimal pl-4 leading-relaxed">
              <li>
                <span>
                  Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A
                  Double-Blind Randomized Controlled Trial. Am J Psychiatry.
                  2022;179(2):132–141.{" "}
                </span>
                <a
                  href="https://pubmed.ncbi.nlm.nih.gov/35624717/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-accent hover:underline"
                >
                  PubMed <ExternalLink className="w-3 h-3" />
                </a>
              </li>
              <li>
                <span>
                  Cole EJ, et al. Stanford Accelerated Intelligent
                  Neuromodulation Therapy for Treatment-Resistant Depression. Am
                  J Psychiatry. 2020;177:716–726.{" "}
                </span>
                <a
                  href="https://pubmed.ncbi.nlm.nih.gov/32552917/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-accent hover:underline"
                >
                  PubMed <ExternalLink className="w-3 h-3" />
                </a>
              </li>
              <li>
                <span>
                  FDA 510(k) clearance K220177, SAINT Neuromodulation
                  System.{" "}
                </span>
                <a
                  href="https://www.accessdata.fda.gov/cdrh_docs/pdf22/K220177.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-accent hover:underline"
                >
                  FDA.gov <ExternalLink className="w-3 h-3" />
                </a>
              </li>
              <li>
                <span>Magnus Medical clinical research. </span>
                <a
                  href="https://www.magnusmedical.com/clinical-research/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-accent hover:underline"
                >
                  magnusmedical.com <ExternalLink className="w-3 h-3" />
                </a>
              </li>
              <li>
                <span>Stanford Brain Stimulation Lab. </span>
                <a
                  href="https://med.stanford.edu/brainstimulationlab.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-accent hover:underline"
                >
                  med.stanford.edu <ExternalLink className="w-3 h-3" />
                </a>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}
